import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { CONTRACT_CLOSURE_INCOMPLETE, isContractClosureIncomplete } from 'helper/contractClosure';
import Button from '../../../../button';
import Modalheader from '../../../components/header';
import ModalBody from '../../../components/body';
import ModalFooter from '../../../components/footer';
import useModal from '../../../useModal';
import UseContext from '../../../../../hooks/useContext';
import ApiDatabase from 'server';
import StyledInput from '../../../../styledInput/StyledInput';
import useUnassignReasons from '../../../../../hooks/useUnassignReasons';
import UnassignReasonForm from '../shared/UnassignReasonForm';

const CANCEL_REASON_NAMES_ONLY_WHEN_ASSIGNED = [
  "L'intérimaire ne va pas assurer la mission",
];

function normalizeReasonLabel(s) {
  return (s || '').toLowerCase().normalize('NFD').replace(/\p{M}/gu, '').trim();
}

const TAB_SELECTED = 'border-2 border-yellow-500 bg-yellow-50';
const TAB_DEFAULT = 'border border-gray-200 bg-white';

const ModalMissionCancel = () => {
  const { toggle } = useModal();
  const { objID, setModalDataReturn, updateMissionStatusLocal, infoMission } = UseContext();
  const [isLoading, setIsLoading] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [reasonSelected, setReasonSelected] = useState('');
  const [isWaiting, setIsWaiting] = useState(true);
  const hasAssignedUser = !!infoMission?.user_assigned;
  const [action, setAction] = useState(hasAssignedUser ? 'unassign' : 'cancel');

  const unassign = useUnassignReasons(hasAssignedUser ? objID : null);

  const reasonsForCancel = useMemo(() => {
    if (hasAssignedUser) return reasons;
    const excluded = new Set(CANCEL_REASON_NAMES_ONLY_WHEN_ASSIGNED.map(normalizeReasonLabel));
    return reasons.filter((r) => !excluded.has(normalizeReasonLabel(r.name)));
  }, [reasons, hasAssignedUser]);

  useEffect(() => {
    if (!reasonSelected) return;
    if (!reasonsForCancel.some((r) => r.name === reasonSelected)) {
      setReasonSelected('');
    }
  }, [reasonsForCancel, reasonSelected]);

  useEffect(() => {
    setIsWaiting(true);
    ApiDatabase.getReasonsMissionSuspension(
      { type: 'cancellation' },
      (data) => { setReasons(data); setIsWaiting(false); },
      () => setIsWaiting(false)
    );
  }, []);

  useEffect(() => {
    setReasonSelected('');
    unassign.reset();
  }, [action]);

  const handleSubmitCancel = () => {
    if (!objID || !reasonSelected) {
      toast.error('Veuillez sélectionner un motif');
      return;
    }
    setIsLoading(true);
    ApiDatabase.updateMissionStatus(
      { idMission: objID, newStatus: 'canceled', reason: reasonSelected },
      (data) => {
        updateMissionStatusLocal(objID, { status: 'canceled', reason: reasonSelected });
        setModalDataReturn('mission_status_updated');
        if (isContractClosureIncomplete(data?.contractClosure)) {
          toast.warning(`La mission a été annulée, ${CONTRACT_CLOSURE_INCOMPLETE}`);
        } else {
          toast.success('La mission a été annulée');
        }
        setIsLoading(false);
        toggle();
      },
      (err) => {
        toast.error("Erreur lors de l'annulation : " + err);
        setIsLoading(false);
      }
    );
  };

  const handleSubmitUnassign = () => {
    if (!objID || !unassign.selectedId || !infoMission?.user_assigned) {
      toast.error('Veuillez sélectionner un motif');
      return;
    }
    if (unassign.noShowError) return;
    setIsLoading(true);
    ApiDatabase.unassignMission(
      {
        idMission: objID,
        userAssigned: infoMission.user_assigned,
        reason: unassign.selectedId,
        subReasons: unassign.subReasonsSelected.length > 0 ? unassign.subReasonsSelected : null,
      },
      () => {
        updateMissionStatusLocal(objID, {
          user_assigned: null,
          user: null,
          isAssigned: false,
        });
        setModalDataReturn('mission unassigned');
        toast.success("L'intérimaire a été désattribué");
        setIsLoading(false);
        toggle();
      },
      (err) => {
        toast.error('Erreur lors de la désattribution : ' + (err.response?.data?.error || err));
        setIsLoading(false);
      }
    );
  };

  const isCurrentWaiting = action === 'cancel' ? isWaiting : unassign.isLoading;
  const isFormValid = action === 'cancel' ? !!reasonSelected : (!!unassign.selectedId && !unassign.noShowError);

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        {hasAssignedUser
          ? action === 'unassign' ? "Désattribuer l'intérimaire" : 'Annuler la mission'
          : 'Annuler la mission'}
      </Modalheader>
      <ModalBody>
        {hasAssignedUser && (
          <div className="flex flex-col gap-1 mb-4">
            <button
              className={`w-full text-left p-4 rounded-none transition-colors ${action === 'unassign' ? TAB_SELECTED : TAB_DEFAULT}`}
              onClick={() => setAction('unassign')}
            >
              <span className="ft-sm font-medium text-gray-700">Dés-attribuer l'intérimaire</span>
              <p className="ft-sm text-gray-500 mt-1">La mission restera en ligne pour être attribuée à quelqu'un d'autre.</p>
            </button>
            <button
              className={`w-full text-left p-4 rounded-none transition-colors ${action === 'cancel' ? TAB_SELECTED : TAB_DEFAULT}`}
              onClick={() => setAction('cancel')}
            >
              <span className="ft-sm font-medium text-gray-700">Annuler la mission</span>
            </button>
          </div>
        )}

        {isCurrentWaiting ? (
          <div className="flex flex-col gap-3 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-20" />
            <div className="h-10 bg-gray-200 rounded w-full" />
          </div>
        ) : action === 'cancel' ? (
          <div className="flex flex-col gap-3">
            <StyledInput
              type="select"
              label="Motif"
              placeholder="Sélectionnez un motif"
              value={reasonSelected}
              onChange={(e) => setReasonSelected(e.target.value)}
              list={[
                { value: '', label: 'Sélectionnez un motif' },
                ...reasonsForCancel.map((r) => ({ value: r.name, label: r.name })),
              ]}
            />
          </div>
        ) : (
          <UnassignReasonForm unassign={unassign} />
        )}
      </ModalBody>
      <ModalFooter>
        <Button size="LG" color="SECONDARY" onClick={toggle}>Fermer</Button>
        {isLoading ? (
          <Button size="LG" color="DISABLED">Confirmation ...</Button>
        ) : (
          <Button
            size="LG"
            color={isFormValid ? 'PRIMARY' : 'DEFAULT'}
            onClick={action === 'cancel' ? handleSubmitCancel : handleSubmitUnassign}
            disabled={!isFormValid || isCurrentWaiting}
          >
            Confirmer
          </Button>
        )}
      </ModalFooter>
    </>
  );
};

export default ModalMissionCancel;
